import { useCallback } from "react";

import { Container, Dialog, Flex, Portal } from "@chakra-ui/react";
import { createOverlay } from "@chakra-ui/react";
import { useFormSubmitError } from "@features/errors/form-errors";
import { useOtpSession } from "@features/two-fa/hooks/use-otp-session";
import { getOtpDeliveryConfig } from "@features/two-fa/utils";
import { ClientGateway } from "@kex/api";
import { useIntl } from "react-intl";

import { useMyAccount } from "@entities/user/hooks/use-my-account";
import { SubPageTemplate } from "@shared/ui/templates/sub-page-template";

import { transactionVerifyOverlayMessages } from "./messages";
import { type RateData, TransactionRate } from "./transaction-rate";
import {
  type TransactionVerifyDetails,
  TransactionVerifyForm,
  type TransactionVerifyFormValues,
} from "./transaction-verify-form";

export interface TransactionVerifyOverlayProps {
  details: TransactionVerifyDetails;
  initialOtp: ClientGateway.OtpModel;
  rate?: RateData;
  resendOtp: (otp: ClientGateway.OtpModel) => Promise<ClientGateway.OtpModel>;
  submit: (
    otpCode: string,
    otp: ClientGateway.OtpModel,
    totpCode: string,
  ) => Promise<void>;
}

export const transactionVerifyOverlay =
  createOverlay<TransactionVerifyOverlayProps>((props) => {
    const {
      details,
      initialOtp,
      rate,
      resendOtp,
      submit,
      onOpenChange,
      ...rest
    } = props;
    const { formatMessage } = useIntl();
    const { myAccount } = useMyAccount(undefined, undefined, true);

    const { blockedUntil, otp, onResend } = useOtpSession({
      initialOtp,
      resend: resendOtp,
    });

    const { contact, label } = getOtpDeliveryConfig(myAccount);
    const otpLabel = formatMessage(label, { contact });

    const handleVerify = useCallback(
      async ({ otpCode, totpCode }: TransactionVerifyFormValues) => {
        await submit(otpCode, otp, totpCode);
      },
      [otp, submit],
    );

    const handleVerifyWithSubmitErrors = useFormSubmitError(handleVerify);

    const handleBack = useCallback(() => {
      onOpenChange?.({ open: false });
    }, [onOpenChange]);
    return (
      <Dialog.Root {...rest} modal={false} preventScroll={false} size="full">
        <Portal>
          <Dialog.Positioner top="16">
            <Dialog.Content backgroundColor="bg" boxShadow="none">
              <Flex direction="column">
                <Container
                  as={Flex}
                  flex={1}
                  flexDirection="column"
                  maxW="lg"
                  pt="8"
                >
                  <SubPageTemplate
                    subTitle={formatMessage(
                      transactionVerifyOverlayMessages.subTitle,
                    )}
                    title={formatMessage(
                      transactionVerifyOverlayMessages.title,
                    )}
                    onBack={handleBack}
                  >
                    {rate && <TransactionRate {...rate} />}
                    <TransactionVerifyForm
                      details={details}
                      otpLabel={otpLabel}
                      resendBlockedUntil={blockedUntil}
                      onBack={handleBack}
                      onResendOTP={onResend}
                      onSubmit={handleVerifyWithSubmitErrors}
                    />
                  </SubPageTemplate>
                </Container>
              </Flex>
            </Dialog.Content>
          </Dialog.Positioner>
        </Portal>
      </Dialog.Root>
    );
  });
